"use client"

import { useEffect, useState } from "react"

import { TypingIndicator } from "@/components/ui/typing-indicator"

/**
 * Staged wait copy, so a slow answer reads as work in progress rather than a hang.
 *
 * The stages run on elapsed time, not on what the agent is doing — nothing reports real
 * progress until the reply lands. So they stay vague, and the lookup wording appears only
 * where a lookup is at least possible. What the agent actually did is shown afterwards,
 * from the real tool calls.
 */
type Phase = { label: string; after: number }

const BASE_PHASES: Phase[] = [
  { label: "Thinking", after: 0 },
  { label: "Putting an answer together", after: 2_500 },
  { label: "Still working on it", after: 12_000 },
]

/** A tool round-trip adds seconds, so the wait gets one more thing to say. */
const TOOL_PHASES: Phase[] = [
  { label: "Thinking", after: 0 },
  { label: "Looking things up", after: 2_500 },
  { label: "Putting an answer together", after: 6_000 },
  { label: "Still working on it", after: 15_000 },
]

export function ThinkingStatus({ withTools = false }: { withTools?: boolean }) {
  const phases = withTools ? TOOL_PHASES : BASE_PHASES
  const [phaseIndex, setPhaseIndex] = useState(0)

  useEffect(() => {
    setPhaseIndex(0)
    const timers = phases
      .slice(1)
      .map((phase, index) => setTimeout(() => setPhaseIndex(index + 1), phase.after))
    return () => timers.forEach(clearTimeout)
  }, [phases])

  const { label } = phases[phaseIndex]

  return (
    <span
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 text-xs text-gray-400"
    >
      <TypingIndicator className="text-gray-300" />
      <span key={label} className="animate-in fade-in duration-500">
        {label}...
      </span>
    </span>
  )
}
